<template>
  <div
    v-if="bestOffer"
    class="product-best-price"
    :class="{ 'product-best-price--special': isDiscountOrOffSeason }"
    data-test="product-best-price"
  >
    <p
      class="product-best-price__label"
      :class="{ 'label-rouge': isOffSeasonProduct }"
      data-test="product-price-from-label"
    >
      {{ priceLabel }}
    </p>
    <p v-if="hasDiscount" class="product-best-price__discount-block" data-test="product-discount-block">
      {{ i18n._("Jusqu'à") }}
    </p>
    <p-price
      :formatted-discount-value="formattedDiscountValue || undefined"
      :formatted-discounted-price="formattedDiscountedPrice || undefined"
      :formatted-h-t-label="formattedHTLabel"
      :formatted-price="formattedPrice"
      :has-discount="hasDiscount"
      :is-off-season="isOffSeasonProduct"
      :packaging-info="packagingInfo"
    ></p-price>
    <div v-if="rpd" class="product-best-price__rpd-per-measure text-nowrap" data-test="product-rpd-price-from">
      + {{ formatPrecisePriceNumber(rpd) }} {{ rpdUnit }}
      <span>{{ i18n._('de RPD') }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { formatMeasureUnit, measureUnit as formatUnit } from '@b2ag/l10n'
import PPrice from '@b2ag/price/src/presenters/p-price/PPrice.vue'
import { useProduct } from '@b2ag/catalog/src/useProduct'
import { useBestPrice } from '@b2ag/price/src/composables/usePrice'
import { i18n } from '@b2ag/locale'
import { formatPrecisePriceNumber } from '@b2ag/l10n'

export default defineComponent({
  name: 'PProductBestPrice',
  components: { PPrice },
  props: {
    productId: { type: String, required: true },
  },
  setup({ productId }) {
    const { bestOffer, isOffSeasonProduct } = useProduct(productId)

    const { hasDiscount, formattedPrice, formattedDiscountedPrice, formattedHTLabel, formattedDiscountValue } =
      // @ts-ignore revoir le mapping
      useBestPrice(bestOffer)
    const rpd = computed(() => {
      return bestOffer.value?.rpdPrice?.value
    })

    const rpdUnit = computed(() => {
      return bestOffer.value && bestOffer.value.rpdUnit
    })

    const priceLabel = computed(() => {
      if (isOffSeasonProduct.value) return i18n._('Offre du moment')
      if (hasDiscount.value) return ''
      return i18n._('À partir de')
    })

    const packagingInfo = computed(
      () =>
        bestOffer.value &&
        bestOffer.value.variantMeasureUnit === 'MG' &&
        bestOffer.value.variantMeasureQuantity &&
        bestOffer.value.variantPackaging &&
        `${formatUnit(bestOffer.value.variantPackaging)} ${i18n._('de')} ${formatMeasureUnit(
          bestOffer.value.variantMeasureQuantity,
          bestOffer.value.variantMeasureUnit,
        )}`,
    )

    const isDiscountOrOffSeason = computed(() => isOffSeasonProduct.value || hasDiscount.value)

    return {
      bestOffer,
      isOffSeasonProduct,
      hasDiscount,
      formattedPrice,
      formattedHTLabel,
      formattedDiscountedPrice,
      formattedDiscountValue,
      rpd,
      rpdUnit,
      priceLabel,
      packagingInfo: packagingInfo.value || '',
      isDiscountOrOffSeason,
      i18n,
      formatPrecisePriceNumber,
    }
  },
})
</script>

<style scoped lang="scss">
.product-best-price {
  border-left: var(--color-text-light) solid 0.3rem;
  padding-left: var(--spacing-2);

  &--special {
    border-color: var(--color-promo);
  }
}

.product-best-price__rpd-per-measure {
  font-size: 16px;
  line-height: 18px;
  color: var(--color-text-light);
  font-weight: bold;
}

.product-best-price__rpd-per-measure > span {
  font-weight: normal;
}

.product-best-price__label {
  font-size: 16px;

  &.label-rouge {
    font-weight: bold;
    color: var(--color-promo);
  }
}

.product-best-price__discount-block {
  color: var(--color-text-light);
}
</style>
